// Caixas/cards de redes sociais e endereço para uso dentro do vídeo Remotion.
// Recebe um objeto `redes` com as redes preenchidas + um objeto `config` opcional.
//
// Props:
//   redes:    { instagram, facebook, whatsapp, tiktok, site, endereco, _config? }
//   fonte
//   formato:  story | feed | tv_horizontal | tv_vertical
//   config:   { bordasArredondadas, corForma, corFonte, habilitados? }
//
// Só exibe redes preenchidas. Chaves começando com "_" são ignoradas (usadas para config interna).

import React from 'react';

const SOCIAL_META = {
  instagram: { label: 'Instagram', icon: 'fa-brands fa-instagram', bg: '#E1306C' },
  facebook:  { label: 'Facebook',  icon: 'fa-brands fa-facebook',  bg: '#1877F2' },
  whatsapp:  { label: 'WhatsApp',  icon: 'fa-brands fa-whatsapp',  bg: '#25D366' },
  tiktok:    { label: 'TikTok',    icon: 'fa-brands fa-tiktok',    bg: '#010101' },
  site:      { label: 'Site',      icon: 'fa-solid fa-globe',      bg: '#73207C' },
  endereco:  { label: 'Endereço',  icon: 'fa-solid fa-location-dot', bg: '#dc2626' },
};

export default function SocialBoxes({ redes = {}, fonte, formato = 'story', config = null }) {
  const cfg = config || (redes && redes._config) || {};
  const habilitados = cfg.habilitados || null; // { instagram: true/false, ... }

  const itens = Object.entries(redes)
    .filter(([k, v]) => !k.startsWith('_') && v && String(v).trim() && (!habilitados || habilitados[k] !== false))
    .map(([k, v]) => ({ tipo: k, valor: String(v).trim() }));

  if (itens.length === 0) return null;

  const isHoriz = formato === 'tv_horizontal';
  const fz = isHoriz ? 26 : 30;
  const cardW = isHoriz ? 240 : 280;

  const corForma = cfg.corForma || null;        // se definida, sobrescreve meta.bg do ícone
  const corFonte = cfg.corFonte || '#0F0A15';
  const arredondado = cfg.bordasArredondadas !== false;
  const cardRadius = arredondado ? 18 : 4;
  const iconRadius = arredondado ? 14 : 4;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isHoriz ? `repeat(${Math.min(itens.length, 5)}, 1fr)` : `repeat(${Math.min(itens.length, 2)}, 1fr)`,
      gap: 18,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 30,
      width: '100%',
      maxWidth: 1400,
      margin: '0 auto',
    }}>
      {itens.map((it) => {
        const meta = SOCIAL_META[it.tipo] || SOCIAL_META.site;
        const bgIcone = corForma || meta.bg;
        return (
          <div
            key={it.tipo}
            style={{
              background: '#fff',
              borderRadius: cardRadius,
              padding: '14px 18px',
              minWidth: cardW,
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              fontFamily: fonte,
              boxShadow: '0 8px 22px rgba(0,0,0,0.25)',
            }}
          >
            <span style={{
              background: bgIcone,
              color: '#fff',
              width: 54, height: 54,
              borderRadius: iconRadius,
              display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: fz * 0.9,
              flexShrink: 0,
            }}>
              <i className={meta.icon} />
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ fontSize: fz * 0.55, color: corFonte, opacity: 0.65, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                {meta.label}
              </span>
              <span style={{
                fontSize: fz, color: corFonte, fontWeight: 800,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: cardW - 80,
              }}>
                {it.valor}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export { SOCIAL_META };

// ---------------------------------------------------------------------------
// SocialFooter: barra fixa no rodapé do vídeo (durante a exibição dos produtos)
// Cards em formato "pílula" como na referência: ícone redondo colorido + texto.
// Endereço aparece em linha separada (centralizado).
// ---------------------------------------------------------------------------
export function SocialFooter({ redes = {}, fonte, formato = 'story', corFundo = null, corTexto = null }) {
  const cfg = (redes && redes._config) || {};
  const habilitados = cfg.habilitados || {};

  const ORDEM = ['instagram', 'whatsapp', 'facebook', 'tiktok', 'site'];
  const itensTopo = ORDEM
    .filter((k) => habilitados[k] !== false && redes[k] && String(redes[k]).trim())
    .map((k) => ({ tipo: k, valor: String(redes[k]).trim() }));

  const enderecoVal =
    habilitados.endereco !== false && redes.endereco && String(redes.endereco).trim()
      ? String(redes.endereco).trim()
      : '';

  if (itensTopo.length === 0 && !enderecoVal) return null;

  const isHoriz = formato === 'tv_horizontal';
  // Rodapé discreto: card colado no texto, com apenas ~3px de sobra acima/abaixo
  const fontTextoMax = isHoriz ? 32 : 36; // texto compacto
  const iconeSize = isHoriz ? 32 : 36;    // ícone do tamanho do texto
  const pillPadH = isHoriz ? 12 : 14;
  const pillPadV = 3;                     // 3px de sobra em cima/baixo
  const gapItens = 8;

  const corFonte = corTexto || cfg.corFonte || '#0F0A15';
  const corFundoPill = corFundo || '#fff';
  const corFormaOverride = cfg.corForma || null;
  const arredondado = cfg.bordasArredondadas !== false;
  const pillRadius = arredondado ? 999 : 8;

  // Distribui as pílulas em linhas que cabem na largura do vídeo.
  // Cada pílula tem largura estimada baseada no comprimento do texto.
  // Se a próxima pílula não cabe na linha atual, ela vai para a próxima.
  // Pílulas dentro da mesma linha usam flex:1 (preenchem 100% da largura).
  const larguraVideo = formato === 'tv_horizontal' ? 1920 : 1080;
  const paddingLateral = 40;
  const larguraDisponivel = larguraVideo - paddingLateral * 2;

  const estimarLargura = (texto) => {
    const larguraIcone = iconeSize;
    const gapInterno = 12;
    // Largura mínima desejável para que o texto não fique apertado/truncado.
    // Considera fonte base de ~28 com coeficiente 0.7 (mesmo do Pill).
    const larguraTexto = String(texto).length * 28 * 0.7;
    return larguraIcone + gapInterno + larguraTexto + pillPadH * 2 + 16;
  };

  const linhas = [];
  let linhaAtual = [];
  let larguraLinha = 0;
  for (const it of itensTopo) {
    const w = estimarLargura(it.valor);
    const novaLargura = larguraLinha + (linhaAtual.length > 0 ? gapItens : 0) + w;
    if (linhaAtual.length > 0 && novaLargura > larguraDisponivel) {
      linhas.push(linhaAtual);
      linhaAtual = [it];
      larguraLinha = w;
    } else {
      linhaAtual.push(it);
      larguraLinha = novaLargura;
    }
  }
  if (linhaAtual.length > 0) linhas.push(linhaAtual);

  const Pill = ({ tipo, valor, fullWidth = false, larguraPill = null }) => {
    const meta = SOCIAL_META[tipo] || SOCIAL_META.site;
    // Ícone sempre na cor oficial da rede social (não herda do tema)
    const bgIcone = meta.bg;
    // Calcula fontSize que se ajusta dentro do card sem cortar.
    // Largura disponível para o texto = largura da pílula - ícone - paddings - gap interno
    const gapInterno = 12;
    const espacoFixo = iconeSize + gapInterno + pillPadH * 2 + 16; // 16px de margem de segurança
    const larguraTexto = Math.max((larguraPill || 200) - espacoFixo, 40);
    // Cada caractere ~ fontSize * 0.7 de largura (fonte bold 800, caracteres @,(),9 são largos).
    const charCount = Math.max(String(valor).length, 1);
    const fontPorLargura = Math.floor(larguraTexto / (charCount * 0.7));
    // Também limita pela altura do card (não pode ultrapassar ~80% da altura)
    const fontPorAltura = Math.floor(iconeSize * 0.85);
    const fontFinal = Math.max(
      10,
      Math.min(fontTextoMax, fontPorLargura, fontPorAltura)
    );
    return (
      <div
        style={{
          background: corFundoPill,
          borderRadius: pillRadius,
          padding: `${pillPadV}px ${pillPadH}px ${pillPadV}px ${pillPadV}px`,
          display: 'flex',
          alignItems: 'center',
          gap: gapInterno,
          fontFamily: fonte,
          boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
          flex: '1 1 0',
          minWidth: 0,
          width: fullWidth ? '100%' : 'auto',
          justifyContent: 'flex-start',
        }}
      >
        <span
          style={{
            background: bgIcone,
            color: '#fff',
            width: iconeSize,
            height: iconeSize,
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: iconeSize * 0.55,
            flexShrink: 0,
          }}
        >
          <i className={meta.icon} />
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: fontFinal,
            color: corFonte,
            fontWeight: 800,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'center',
            lineHeight: 1.1,
          }}
        >
          {valor}
        </span>
      </div>
    );
  };

  // Posição vertical do rodapé (px do bottom) ajustada por formato.
  let bottomPx = 40;
  if (formato === 'feed') bottomPx = 20;
  else if (formato === 'story' || formato === 'tv_vertical') bottomPx = 15;

  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: bottomPx,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 10,
        padding: '0 20px',
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      {linhas.map((linha, idx) => {
        // Largura individual de cada pílula nessa linha (dividida igualmente)
        const larguraPill =
          (larguraDisponivel - gapItens * Math.max(linha.length - 1, 0)) / linha.length;
        return (
          <div
            key={idx}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: gapItens,
              width: '100%',
            }}
          >
            {linha.map((it) => (
              <Pill
                key={it.tipo}
                tipo={it.tipo}
                valor={it.valor}
                fullWidth={linha.length === 1}
                larguraPill={larguraPill}
              />
            ))}
          </div>
        );
      })}
      {enderecoVal && (
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Pill tipo="endereco" valor={enderecoVal} fullWidth larguraPill={larguraDisponivel} />
        </div>
      )}
    </div>
  );
}
